import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of, map, catchError } from 'rxjs';
import { environment } from '../../environments/environment';
import { Region, ClientInfo, RegionLookupResult, InvitationCodeResult } from '../shared/types/redirect.model';

@Injectable({ providedIn: 'root' })
export class RegionService {
  private readonly http = inject(HttpClient);

  lookupByEmail(email: string): Observable<RegionLookupResult> {
    const usLookup$ = this.checkRegion(environment.apiUsBaseUrl, email, environment.portalUsUrl);
    const caLookup$ = this.checkRegion(environment.apiCaBaseUrl, email, environment.portalCaUrl);

    return forkJoin([usLookup$, caLookup$]).pipe(
      map(([usClients, caClients]) => {
        const allClients = [...usClients, ...caClients];
        const foundInMultipleClients = allClients.length > 1;

        return {
          clients: allClients,
          foundInMultipleClients
        };
      })
    );
  }

  private checkRegion(apiBaseUrl: string, email: string, portalUrl: string): Observable<ClientInfo[]> {
    const url = `${apiBaseUrl}/api/v1/MemberClient/GetClientByUsernameEmail`;
    return this.http.get<{ clientId: number | null; region: string; clientName: string | null }[]>(
      url,
      { params: { UsernameOrEmail: email } }
    ).pipe(
      map(response => {
        if (!Array.isArray(response)) {
          return [];
        }
        return response.map(client => ({
          clientId: client.clientId,
          region: client.region.toLowerCase() as Region,
          clientName: client.clientName,
          portalUrl: portalUrl
        }));
      }),
      catchError(() => of([]))
    );
  }

  lookupByInvitationCode(invitationCode: string): Observable<InvitationCodeResult[]> {
    const usLookup$ = this.checkInvitationCode(environment.apiUsBaseUrl, invitationCode, environment.portalUsUrl);
    const caLookup$ = this.checkInvitationCode(environment.apiCaBaseUrl, invitationCode, environment.portalCaUrl);

    return forkJoin([usLookup$, caLookup$]).pipe(
      map(([usResult, caResult]) => {
        const results: InvitationCodeResult[] = [];
        if (usResult) results.push(usResult);
        if (caResult) results.push(caResult);
        return results;
      })
    );
  }

  private checkInvitationCode(apiBaseUrl: string, invitationCode: string, portalUrl: string): Observable<InvitationCodeResult | null> {
    const url = `${apiBaseUrl}/api/v1/MemberClient/GetClientByInvitationCode`;
    return this.http.get<any>(url, { params: { invitationCode } }).pipe(
      map(response => {
        // Check if response has actual data
        const data = response?.result || response;
        if (data == null){
          return null;
        }
        return {
          ...data,
          portalUrl: portalUrl
        };
      }),
      catchError(() => of(null))
    );
  }
}

